import { useQueryClient } from "@tanstack/react-query";
import React, { useEffect, useState } from "react";
import { useParams } from "react-router";
import { useWindowSize } from "../../../../../Helpers";
import Button from "@components/Button";
import "./style.scss";
import LinearGradientProgressBar from "@components/LinearGradientProgressBar";
import StickyButton from "../../../../../components/StickyButton";
import PagesHeader from "@components/PagesHeader";
import StickyContent from "@components/StickyContent";
import {
  DndContext,
  useDraggable,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import DOMPurify from "dompurify";


function DraggablePart({ item, status, gameCompleted }) {
  const disabled = gameCompleted || status === "correct" || status === "wrong";

  const { attributes, listeners, setNodeRef, transform, isDragging } =
    useDraggable({ id: item.id, disabled });

  const outlineColor =
    status === "correct"
      ? "#6BBF41"
      : status === "wrong"
        ? "#FF8181"
        : "transparent";

  const style = {
    transform: transform
      ? `translate3d(${transform.x}px, ${transform.y}px, 0)`
      : undefined,
    opacity: isDragging ? 0.7 : 1,
    cursor: disabled ? "default" : "grab",
    outline: `4px solid ${outlineColor}`,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className="draggable-img-wrapper"
      {...(!disabled ? listeners : {})}
      {...(!disabled ? attributes : {})}
    >
      <img src={item.img} alt={item.id} draggable={false} />
    </div>
  );
}

export default function DragNDrop({
  setCurrentQuestion,
  currentQuestion,
  setAnswers,
  answers,
  sendAnswers,
}) {
  const { ident } = useParams();
  const { width } = useWindowSize();
  const queryClient = useQueryClient();
  const gameQuestionsQuery = queryClient.getQueryData([
    "game-questions",
    ident,
  ]);

  const XGA = width >= 1020;

  const {
    dragInit,
    dragSuccess,
    backgroundImage,
    dragFail,
    Mask,
    draggable,
    targets,
  } = gameQuestionsQuery.content[currentQuestion];

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 5,
      },
    })
  );

  const [feedback, setFeedback] = useState(dragInit);

  const [assignments, setAssignments] = useState(() =>
    Object.fromEntries(targets.map((t) => [t.id, null]))
  );

  const [itemStates, setItemStates] = useState(() =>
    Object.fromEntries(draggable.map((d) => [d.id, "idle"]))
  );

  const [activeMask, setActiveMask] = useState(null);

  const gameCompleted = targets.every((t) => {
    const itemId = assignments[t.id];
    if (!itemId) return false;
    return itemStates[itemId] === "correct";
  });

  function handleDragStart(event) {
    const itemId = event.active.id;
    const item = draggable.find((d) => d.id === itemId);
    setActiveMask(item?.mask ?? null);
  }

  function handleDragEnd(event) {
    const { active, over } = event;
    const itemId = active.id;

    setActiveMask(null);

    const item = draggable.find((d) => d.id === itemId);
    if (!item) return;

    const isCorrectPiece = Boolean(item.target);

    if (gameCompleted || (itemStates[itemId] && itemStates[itemId] !== "idle"))
      return;

    if (!over) {
      if (isCorrectPiece) {
        setFeedback(dragInit);
        return;
      } else {
        setFeedback(dragFail);
        setItemStates((prev) => ({
          ...prev,
          [itemId]: "wrong",
        }));
        return;
      }
    }

    const targetId = over.id;

    if (!item.target || item.target !== targetId) {
      if (isCorrectPiece) {
        setFeedback(dragInit);
        return;
      } else {
        setFeedback(dragFail);
        setItemStates((prev) => ({
          ...prev,
          [itemId]: "wrong",
        }));
        return;
      }
    }

    setFeedback(dragSuccess);

    setAssignments((prev) => {
      const updated = { ...prev };

      Object.keys(updated).forEach((tid) => {
        if (updated[tid] === itemId) updated[tid] = null;
      });

      updated[targetId] = itemId;
      return updated;
    });

    setItemStates((prev) => ({
      ...prev,
      [itemId]: "correct",
    }));
  }

  useEffect(() => {
    setAssignments(
      Object.fromEntries(targets.map((t) => [t.id, null]))
    );
    setItemStates(
      Object.fromEntries(draggable.map((d) => [d.id, "idle"]))
    );
    setFeedback(dragInit);
  }, [currentQuestion, dragInit, draggable, targets]);

  function handleNextButtonClick() {
    const selectedItems = Object.values(assignments).filter(Boolean);

    const selectedUUIDs = selectedItems.map(itemId => {
      const item = draggable.find(d => d.id === itemId);
      return item?.uuid;
    }).filter(Boolean);

    const newAnswers = [
      ...answers,
      {
        question_ident: gameQuestionsQuery.content[currentQuestion].ident,
        answer_ident: [selectedUUIDs],
      },
    ];

    setAnswers(newAnswers);
    setCurrentQuestion(prev => prev + 1);

    console.log(newAnswers)
  }

  return (
    <section className={`game-page drag-n-drop ${ident}`}>
      <div>
        <PagesHeader back>
          <p
            dangerouslySetInnerHTML={{
              __html: DOMPurify.sanitize(gameQuestionsQuery?.title),
            }}
          ></p>
        </PagesHeader>
        <div className="main-game-wrapper">
          <div className="desktop-wrapper">
            <LinearGradientProgressBar
              value={currentQuestion / gameQuestionsQuery.content.length}
            />
            <div className="game-question">
              {gameQuestionsQuery.content[currentQuestion].question && (
                <h2
                  className="question"
                  dangerouslySetInnerHTML={{
                    __html:
                      DOMPurify.sanitize(gameQuestionsQuery.content[currentQuestion]?.question),
                  }}
                />
              )}
            </div>
          </div>
          <div className="side-img">
            <div className="drag-n-drop-wrapper">
              <DndContext
                sensors={sensors}
                onDragStart={handleDragStart}
                onDragEnd={handleDragEnd}
              >
                <div className="device-image-wrapper">
                  <img
                    src={backgroundImage}
                    alt="Device"
                    className="background-img"
                    draggable={false}
                  />

                  <Mask activeMask={activeMask} />

                  {targets.map((t) => {
                    const itemId = assignments[t.id];
                    if (!itemId) return null;

                    const item = draggable.find((d) => d.id === itemId);
                    if (!item || item.target !== t.id) return null;

                    return (
                      <img
                        key={t.id}
                        src={t?.successImage}
                        alt=""
                        className={`target-success-img target-success-img-${t.id}`}
                        draggable={false}
                      />
                    );
                  })}
                </div>

                <div className="draggable-container">
                  {draggable.map((item) => (
                    <DraggablePart
                      key={item.id}
                      item={item}
                      status={itemStates[item.id] || "idle"}
                      gameCompleted={gameCompleted}
                    />
                  ))}
                </div>

                <p className="drag-feedback">
                  {feedback || "&nbsp;"}
                </p>
              </DndContext>
            </div>
            {XGA && (
              <StickyContent>
                <div className="desktop-buttons">
                  <Button
                    disabled={!gameCompleted}
                    action={handleNextButtonClick}
                  >
                    {gameQuestionsQuery.content[currentQuestion]
                      .nextButtonCopy ?? "Nächste"}
                  </Button>
                </div>
              </StickyContent>
            )}
          </div>
        </div>
      </div>

      {!XGA && (
        <StickyButton
          disabled={!gameCompleted}
          action={handleNextButtonClick}
        >
          {gameQuestionsQuery.content[currentQuestion].nextButtonCopy ??
            "Nächste"}
        </StickyButton>
      )}
    </section>
  );
}
